import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus,
    Trash2,
    Coffee,
    Croissant,
    X,
    Check,
    Copy,
    ArrowUp,
    ArrowDown,
    Image as ImageIcon,
    ChevronRight,
} from "lucide-react";
import { buildAdminApi } from "../../lib/admin";
import { toast } from "sonner";

const AdminMenu = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminApi
            .get("/admin/menu")
            .then((r) => setItems(r.data.items))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [slug]);

    const coffee = items
        .filter((i) => i.kind === "coffee")
        .sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));
    const food = items
        .filter((i) => i.kind === "food")
        .sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));

    const save = async (form) => {
        try {
            if (editing === "new") {
                await adminApi.post("/admin/menu", form);
                toast.success("Item added");
            } else {
                await adminApi.patch(`/admin/menu/${editing.id}`, form);
                toast.success("Saved");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete ${item.name}?`)) return;
        try {
            await adminApi.delete(`/admin/menu/${item.id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const duplicate = async (item) => {
        try {
            await adminApi.post(`/admin/menu/${item.id}/duplicate`);
            toast.success(`Duplicated ${item.name}`);
            load();
        } catch {
            toast.error("Duplicate failed");
        }
    };

    // Quick sold-out toggle — one-tap from the list.
    const toggleSoldOut = async (item) => {
        try {
            await adminApi.patch(`/admin/menu/${item.id}`, { sold_out: !item.sold_out });
            toast.success(item.sold_out ? "Back in stock" : "Marked sold out");
            load();
        } catch {
            toast.error("Update failed");
        }
    };

    // Swap two items' sort_order and persist in bulk.
    const move = async (list, index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= list.length) return;
        const a = list[index];
        const b = list[target];
        const aOrder = a.sort_order ?? 100;
        const bOrder = b.sort_order ?? 100;
        // Optimistic UI: swap locally first
        setItems((prev) =>
            prev.map((it) => {
                if (it.id === a.id) return { ...it, sort_order: bOrder };
                if (it.id === b.id) return { ...it, sort_order: aOrder };
                return it;
            })
        );
        try {
            await adminApi.patch("/admin/menu/reorder", {
                items: [
                    { id: a.id, sort_order: bOrder },
                    { id: b.id, sort_order: aOrder },
                ],
            });
        } catch {
            toast.error("Reorder failed");
            load();
        }
    };

    return (
        <div data-testid="admin-menu-page">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                <div>
                    <h1 className="font-display font-black text-3xl">Menu</h1>
                    <p className="text-sm text-[#8C7A72] mt-1">
                        Tap a row to edit. Drag with the arrows to reorder. Sold-out is one tap.
                    </p>
                </div>
                <button
                    data-testid="add-item-btn"
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF4F18] text-white font-display font-bold text-sm active:scale-95"
                >
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                    Add item
                </button>
            </div>

            {loading ? (
                <div className="text-center py-20 text-[#8C7A72]">Loading…</div>
            ) : (
                <div className="grid gap-6">
                    <Section
                        title="Coffee"
                        icon={Coffee}
                        items={coffee}
                        onEdit={setEditing}
                        onDelete={remove}
                        onDuplicate={duplicate}
                        onToggleSoldOut={toggleSoldOut}
                        onMove={move}
                    />
                    <Section
                        title="Food"
                        icon={Croissant}
                        items={food}
                        onEdit={setEditing}
                        onDelete={remove}
                        onDuplicate={duplicate}
                        onToggleSoldOut={toggleSoldOut}
                        onMove={move}
                    />
                </div>
            )}

            <AnimatePresence>
                {editing && (
                    <EditSheet
                        item={editing === "new" ? null : editing}
                        onClose={() => setEditing(null)}
                        onSave={save}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

// ==================== SECTION LIST ====================

const Section = ({ title, icon: Icon, items, onEdit, onDelete, onDuplicate, onToggleSoldOut, onMove }) => (
    <div className="rounded-3xl bg-white border border-[#E8DCCB] p-6">
        <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-2 font-display font-black text-xl">
                <Icon className="h-5 w-5 text-[#FF4F18]" strokeWidth={2.5} />
                {title}
            </div>
            <div className="text-xs text-[#8C7A72]">{items.length} items</div>
        </div>
        {items.length === 0 ? (
            <div className="text-sm text-[#8C7A72] py-4 text-center">
                No items yet. Tap "Add item".
            </div>
        ) : (
            <div className="flex flex-col divide-y divide-[#E8DCCB]/70">
                {items.map((it, i) => (
                    <MenuRow
                        key={it.id}
                        item={it}
                        isFirst={i === 0}
                        isLast={i === items.length - 1}
                        onEdit={() => onEdit(it)}
                        onDelete={() => onDelete(it)}
                        onDuplicate={() => onDuplicate(it)}
                        onToggleSoldOut={() => onToggleSoldOut(it)}
                        onMoveUp={() => onMove(items, i, -1)}
                        onMoveDown={() => onMove(items, i, 1)}
                    />
                ))}
            </div>
        )}
    </div>
);

// ==================== ONE MENU ROW ====================

const MenuRow = ({
    item,
    isFirst,
    isLast,
    onEdit,
    onDelete,
    onDuplicate,
    onToggleSoldOut,
    onMoveUp,
    onMoveDown,
}) => {
    const priceLabel =
        item.kind === "coffee" && item.sizes
            ? `S $${(item.sizes.S ?? 0).toFixed(2)} · L $${(item.sizes.L ?? 0).toFixed(2)}`
            : item.price !== null && item.price !== undefined
            ? `$${item.price.toFixed(2)}`
            : "—";
    const modCount = (item.modifiers || []).length;

    return (
        <div
            className={`flex items-center gap-2 py-3 ${item.sold_out ? "opacity-60" : ""}`}
            data-testid={`row-${item.id}`}
        >
            {/* Reorder arrows */}
            <div className="flex flex-col gap-0.5 shrink-0">
                <button
                    disabled={isFirst}
                    onClick={onMoveUp}
                    data-testid={`up-${item.id}`}
                    className="h-6 w-6 rounded-md hover:bg-[#F5EDE0] disabled:opacity-20 flex items-center justify-center"
                >
                    <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
                <button
                    disabled={isLast}
                    onClick={onMoveDown}
                    data-testid={`down-${item.id}`}
                    className="h-6 w-6 rounded-md hover:bg-[#F5EDE0] disabled:opacity-20 flex items-center justify-center"
                >
                    <ArrowDown className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
            </div>

            {/* Image thumb */}
            {item.image_url ? (
                <img
                    src={item.image_url}
                    alt=""
                    className="h-12 w-12 rounded-xl object-cover shrink-0 border border-[#E8DCCB]"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                />
            ) : (
                <div className="h-12 w-12 rounded-xl bg-[#F5EDE0] flex items-center justify-center shrink-0">
                    <ImageIcon className="h-4 w-4 text-[#8C7A72]" strokeWidth={2} />
                </div>
            )}

            {/* Main */}
            <button onClick={onEdit} className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold truncate">{item.name}</div>
                    {!item.active && (
                        <span className="text-[10px] uppercase tracking-widest text-[#8C7A72] font-bold px-2 py-0.5 rounded-full bg-[#F5EDE0]">
                            Hidden
                        </span>
                    )}
                    {item.sold_out && (
                        <span
                            data-testid={`sold-out-badge-${item.id}`}
                            className="text-[10px] uppercase tracking-widest text-[#c73810] font-bold px-2 py-0.5 rounded-full bg-[#FF4F18]/10 border border-[#FF4F18]/30"
                        >
                            Sold out
                        </span>
                    )}
                    {modCount > 0 && (
                        <span className="text-[10px] uppercase tracking-widest text-[#0f6b41] font-bold px-2 py-0.5 rounded-full bg-[#1B995E]/10">
                            {modCount} mod{modCount === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
                <div className="text-xs text-[#8C7A72] mt-0.5">{priceLabel}</div>
            </button>

            {/* Actions */}
            <button
                data-testid={`soldout-toggle-${item.id}`}
                onClick={onToggleSoldOut}
                title={item.sold_out ? "Mark back in stock" : "Mark sold out"}
                className={`h-9 px-3 rounded-full text-xs font-display font-bold flex items-center gap-1 ${
                    item.sold_out
                        ? "bg-[#FF4F18] text-white"
                        : "bg-[#F5EDE0] text-[#2C1A12] hover:bg-[#E8DCCB]"
                }`}
            >
                {item.sold_out ? "Restock" : "Sold out"}
            </button>
            <button
                data-testid={`duplicate-${item.id}`}
                onClick={onDuplicate}
                className="h-9 w-9 rounded-full bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center opacity-70 hover:opacity-100"
                title="Duplicate"
            >
                <Copy className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <button
                data-testid={`delete-${item.id}`}
                onClick={onDelete}
                className="h-9 w-9 rounded-full bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center opacity-70 hover:opacity-100"
                title="Delete"
            >
                <Trash2 className="h-4 w-4" strokeWidth={2.5} />
            </button>
        </div>
    );
};

// ==================== EDIT / CREATE SHEET ====================

const EditSheet = ({ item, onClose, onSave }) => {
    const isNew = !item;
    const [kind, setKind] = useState(item?.kind || "coffee");
    const [name, setName] = useState(item?.name || "");
    const [description, setDescription] = useState(item?.description || "");
    const [priceS, setPriceS] = useState(item?.sizes?.S ?? "");
    const [priceL, setPriceL] = useState(item?.sizes?.L ?? "");
    const [price, setPrice] = useState(item?.price ?? "");
    const [active, setActive] = useState(item?.active !== false);
    const [soldOut, setSoldOut] = useState(item?.sold_out || false);
    const [imageUrl, setImageUrl] = useState(item?.image_url || "");
    const [modifiers, setModifiers] = useState(item?.modifiers || []);
    const [sortOrder] = useState(item?.sort_order ?? 100);

    const addModifier = () => {
        const nextId = `mod_${Date.now()}`;
        setModifiers([...modifiers, { id: nextId, name: "", price_delta: 0 }]);
    };
    const updateModifier = (idx, patch) => {
        setModifiers(modifiers.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    };
    const removeModifier = (idx) => {
        setModifiers(modifiers.filter((_, i) => i !== idx));
    };

    const submit = () => {
        if (!name.trim()) return toast.error("Enter a name");
        // Sanitize modifiers: drop rows with empty name
        const cleanMods = modifiers
            .filter((m) => m.name && m.name.trim())
            .map((m) => ({
                id: (m.id || `mod_${Math.random().toString(36).slice(2, 8)}`).slice(0, 30),
                name: m.name.trim().slice(0, 40),
                price_delta: Number(m.price_delta) || 0,
            }));

        const payload = {
            name: name.trim(),
            active,
            sold_out: soldOut,
            image_url: imageUrl.trim() || null,
            modifiers: cleanMods,
            description: description.trim() || null,
            sort_order: Number(sortOrder),
        };
        if (isNew) payload.kind = kind;
        if (kind === "coffee") {
            const s = parseFloat(priceS);
            const l = parseFloat(priceL);
            if (isNaN(s) || isNaN(l) || s <= 0 || l <= 0) return toast.error("Enter valid prices");
            payload.sizes = { S: s, L: l };
            payload.price = null;
        } else {
            const p = parseFloat(price);
            if (isNaN(p) || p <= 0) return toast.error("Enter valid price");
            payload.price = p;
            payload.sizes = null;
        }
        onSave(payload);
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-[#2C1A12]/60 z-40"
                onClick={onClose}
            />
            <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                className="fixed inset-x-0 bottom-0 z-50 bg-[#F9F6F0] rounded-t-[32px] max-w-lg mx-auto max-h-[92vh] flex flex-col"
                data-testid="edit-sheet"
            >
                <div className="flex items-start justify-between p-6 pb-4 shrink-0">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                            {isNew ? "New item" : "Edit"}
                        </div>
                        <div className="font-display font-black text-2xl mt-1 truncate">{name || "…"}</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center shrink-0"
                    >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                </div>

                <div className="overflow-y-auto px-6 pb-4 flex-1">
                    {isNew && (
                        <div className="mb-4">
                            <Label>Type</Label>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                {[{ id: "coffee", label: "Coffee (S/L)" }, { id: "food", label: "Food (flat)" }].map((k) => (
                                    <button
                                        key={k.id}
                                        data-testid={`kind-${k.id}`}
                                        onClick={() => setKind(k.id)}
                                        className={`py-3 rounded-2xl font-display font-bold text-sm border-2 ${
                                            kind === k.id
                                                ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                : "border-[#E8DCCB] bg-white"
                                        }`}
                                    >
                                        {k.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <Field label="Name">
                        <input
                            data-testid="name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full py-3 px-4 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none font-display font-bold text-lg"
                            placeholder="e.g. Flat White"
                        />
                    </Field>

                    <Field label="Description (optional)">
                        <input
                            data-testid="desc-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={280}
                            className="w-full py-3 px-4 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none text-sm"
                            placeholder="Silky milk, single origin espresso"
                        />
                    </Field>

                    {kind === "coffee" ? (
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Small">
                                <input
                                    data-testid="price-s"
                                    type="number"
                                    step="0.10"
                                    value={priceS}
                                    onChange={(e) => setPriceS(e.target.value)}
                                    className="w-full py-3 px-4 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none font-display font-bold text-lg"
                                    placeholder="4.50"
                                />
                            </Field>
                            <Field label="Large">
                                <input
                                    data-testid="price-l"
                                    type="number"
                                    step="0.10"
                                    value={priceL}
                                    onChange={(e) => setPriceL(e.target.value)}
                                    className="w-full py-3 px-4 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none font-display font-bold text-lg"
                                    placeholder="5.50"
                                />
                            </Field>
                        </div>
                    ) : (
                        <Field label="Price">
                            <input
                                data-testid="price"
                                type="number"
                                step="0.10"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                className="w-full py-3 px-4 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none font-display font-bold text-lg"
                                placeholder="4.50"
                            />
                        </Field>
                    )}

                    <Field label="Image URL (optional)">
                        <input
                            data-testid="image-input"
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            type="url"
                            placeholder="https://images.unsplash.com/…"
                            className="w-full py-3 px-4 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none text-sm"
                        />
                        {imageUrl && (
                            <div className="mt-2 flex items-center gap-3">
                                <img
                                    src={imageUrl}
                                    alt="Preview"
                                    className="h-16 w-16 rounded-xl object-cover border border-[#E8DCCB]"
                                    onError={(e) => (e.currentTarget.style.opacity = "0.2")}
                                />
                                <div className="text-xs text-[#8C7A72]">Preview</div>
                            </div>
                        )}
                    </Field>

                    {/* MODIFIERS */}
                    <div className="mt-4 mb-3">
                        <div className="flex items-center justify-between mb-2">
                            <Label>Modifiers (optional)</Label>
                            <button
                                data-testid="add-modifier-btn"
                                type="button"
                                onClick={addModifier}
                                className="text-xs font-display font-bold text-[#FF4F18] hover:underline flex items-center gap-1"
                            >
                                <Plus className="h-3 w-3" strokeWidth={2.5} />
                                Add modifier
                            </button>
                        </div>
                        <p className="text-xs text-[#8C7A72] mb-3">
                            Extras a customer can add — e.g. "Extra shot +$0.80", "Caramel syrup +$0.50".
                        </p>
                        {modifiers.length === 0 ? (
                            <div className="text-xs text-[#8C7A72] text-center py-4 rounded-2xl bg-white border-2 border-dashed border-[#E8DCCB]">
                                No modifiers yet. Perfect for coffees you sell "as is".
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {modifiers.map((m, idx) => (
                                    <div key={idx} className="flex items-center gap-2" data-testid={`modifier-row-${idx}`}>
                                        <input
                                            value={m.name}
                                            onChange={(e) => updateModifier(idx, { name: e.target.value })}
                                            placeholder="Extra shot"
                                            className="flex-1 py-2.5 px-3 rounded-xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none text-sm"
                                        />
                                        <div className="relative w-24">
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8C7A72] text-sm">$</span>
                                            <input
                                                type="number"
                                                step="0.10"
                                                value={m.price_delta}
                                                onChange={(e) => updateModifier(idx, { price_delta: e.target.value })}
                                                placeholder="0.80"
                                                className="w-full py-2.5 pl-6 pr-2 rounded-xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none text-sm font-mono"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeModifier(idx)}
                                            className="h-9 w-9 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center opacity-70 hover:opacity-100"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                            data-testid="toggle-active"
                            type="button"
                            onClick={() => setActive(!active)}
                            className={`py-3 rounded-2xl font-display font-bold text-sm border-2 ${
                                active
                                    ? "border-[#1B995E] bg-[#1B995E]/10 text-[#1B995E]"
                                    : "border-[#E8DCCB] bg-white text-[#8C7A72]"
                            }`}
                        >
                            {active ? "✓ Visible" : "Hidden"}
                        </button>
                        <button
                            data-testid="toggle-sold-out"
                            type="button"
                            onClick={() => setSoldOut(!soldOut)}
                            className={`py-3 rounded-2xl font-display font-bold text-sm border-2 ${
                                soldOut
                                    ? "border-[#FF4F18] bg-[#FF4F18]/10 text-[#c73810]"
                                    : "border-[#E8DCCB] bg-white text-[#8C7A72]"
                            }`}
                        >
                            {soldOut ? "Sold out" : "In stock"}
                        </button>
                    </div>
                </div>

                <div className="p-6 pt-3 shrink-0 border-t border-[#E8DCCB] bg-[#F9F6F0]">
                    <button
                        data-testid="save-btn"
                        onClick={submit}
                        className="w-full py-4 rounded-full bg-[#FF4F18] text-white font-display font-bold text-lg active:scale-[0.98] inline-flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(255,79,24,0.32)]"
                    >
                        <Check className="h-5 w-5" strokeWidth={2.5} />
                        {isNew ? "Add item" : "Save changes"}
                    </button>
                </div>
            </motion.div>
        </>
    );
};

const Label = ({ children }) => (
    <span className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
        {children}
    </span>
);

const Field = ({ label, children }) => (
    <div className="mb-3">
        <Label>{label}</Label>
        <div className="mt-2">{children}</div>
    </div>
);

export default AdminMenu;
